import {
  AtAGlance,
  AudioPlayer,
  Button,
  Card,
  CardHeader,
  CallDetailBody,
  CallDetailColumns,
  CallDetailNav,
  CallDetailPage,
  CallDetailRail,
  CallDetailTranscript,
  NextStepBanner,
  DashboardHero,
  GlanceRow,
  HeroCell,
  IconAlert,
  IconBell,
  IconCalendar,
  IconCheck,
  IconClock,
  InlineAlert,
  KvRow,
  KvTable,
  OutcomeHero,
  type OutcomeVariant,
  PageHeader,
  ProgressBar,
  CallPulse,
  RecentCallLog,
  RelatedCallRow,
  RelatedCallsCard,
  type RelatedCallDotTone,
  ResultPill,
  type PillTone,
} from "@constructorfleet/ui/design-system";
import { normalizeUsPhoneNumber } from "@constructorfleet/ui/design-system";
import { FormField, TextArea, TextInput, useToast } from "@constructorfleet/ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  type BhgCallEvent,
  type BhgCallRecord,
  type BhgScheduleSummary,
  type BhgSelfReportedScreen,
  liveBhgCallDashboardProvider,
} from "../bhg-call-dashboard-api";
import type { BhgCallDashboardProvider, BhgClinicOption } from "../types";
import {
  DEFAULT_BHG_NOTIFICATION_TIME,
  DEFAULT_BHG_NOTIFICATION_TIMEZONE,
  isValidBhgNotificationTime,
  buildMonthlyScreenMessage,
  buildMonthlyScreenHeadline,
  resolveMonthlyScreenNudgeUrgency,
} from "@constructorfleet/bhg-product-core";
import { BHG_ROUTES } from "@constructorfleet/bhg-product-core";
import { utils, writeFileXLSX } from "xlsx";
import type { BhgCallProgressStep } from "../components/BhgCallProgressDiagram";
import {
  BhgClinicSettings,
  ClinicHoursBlock,
  parseHoursJson,
  getHoursForDay,
  getWeekdayKey,
} from "../components/BhgClinicSettings";
import {
  BhgNotificationSettings,
  type BhgNotificationSettingsMessages,
} from "../components/BhgNotificationSettings";
import {
  BhgNotificationScheduleSettings,
  type BhgNotificationScheduleSettingsMessages,
} from "../components/BhgNotificationScheduleSettings";

const FALLBACK_POLL_INTERVAL_MS = 15_000;

function formatDateLabel(value: string) {
  // Date-only strings (YYYY-MM-DD) are parsed as UTC by JS, which shifts
  // to the previous day in timezones behind UTC. Treat them as local dates.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(value + "T00:00:00");
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  let date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    // Handle "YYYY-MM-DD HH:MM UTC" format (not universally parseable by Date)
    const utcMatch = value.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) UTC$/);
    if (utcMatch) date = new Date(`${utcMatch[1]}T${utcMatch[2]}:00.000Z`);
  }
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLiveRefreshLabel(updatedAt: string | null) {
  if (!updatedAt) return "Waiting for the first dashboard refresh.";
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return `Last refresh ${updatedAt}.`;
  return `Last refresh ${date.toLocaleString()}.`;
}

function parseCallStartedAt(value: string) {
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const match = value.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) UTC$/);
  if (!match) return null;

  const fallback = new Date(`${match[1]}T${match[2]}:00.000Z`);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDuration(totalSeconds: number) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function describeDashboardNotificationChannels(channels: ("sms" | "email")[]) {
  const normalized = [...new Set(channels.map((channel) => channel.trim().toLowerCase()))];
  if (normalized.includes("sms") && normalized.includes("email")) return "SMS + Email";
  if (normalized.includes("sms")) return "SMS only";
  if (normalized.includes("email")) return "Email only";
  return "Needs setup";
}

export function formatNextCallAtLabel(
  scheduleSummary: BhgScheduleSummary | null,
  notificationTime: string
) {
  const timeLabel = formatNotificationTime(notificationTime);
  if (!timeLabel) return "Today";
  if ((scheduleSummary?.today.scheduledLabels.length ?? 0) > 0) return `Today @ ${timeLabel}`;
  return `Tomorrow @ ${timeLabel}`;
}

function formatNotificationTime(time: string) {
  if (!isValidBhgNotificationTime(time)) return null;
  const [hoursRaw, minutesRaw] = time.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const meridiem = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hours12}:${String(minutes).padStart(2, "0")} ${meridiem}`;
}

export function formatScheduleStatusLabel(options: {
  day: "today" | "tomorrow";
  scheduledLabels: string[];
  unscheduledLabels: string[];
}) {
  if (options.scheduledLabels.length > 0) return options.scheduledLabels.join(" + ");
  if (options.unscheduledLabels.length > 0) return "Not scheduled";
  return "Unknown";
}

export function extractDosingEndTime(hoursRange: string | null): string | null {
  if (!hoursRange) return null;
  const match = hoursRange.match(/-\s*(.+)$/);
  return match ? match[1].trim() : null;
}

function formatByDosingEnd(hoursRange: string | null): string {
  const endTime = extractDosingEndTime(hoursRange);
  return endTime ? ` by ${endTime}` : "";
}

function formatScheduleDateLabel(isoDate: string | null) {
  if (!isoDate) return "Schedule";
  const parts = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!parts) return isoDate;
  const parsed = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

export function getProminentScheduleCallout(
  scheduleSummary: BhgScheduleSummary | null,
  options?: { todayDosingHours?: string | null; tomorrowDosingHours?: string | null }
) {
  if (!scheduleSummary) return null;

  if (scheduleSummary.today.scheduledLabels.length > 0) {
    const byLabel = formatByDosingEnd(options?.todayDosingHours ?? null);
    return {
      dayLabel: "Today",
      dateLabel: `Today · ${formatScheduleDateLabel(scheduleSummary.todayIsoDate)}`,
      statusLabel: scheduleSummary.today.scheduledLabels.join(" + "),
      emphasis: "urgent" as const,
      helpText: `You are scheduled to visit the treatment center today${byLabel}.`,
    };
  }

  if (scheduleSummary.tomorrow.scheduledLabels.length > 0) {
    const byLabel = formatByDosingEnd(options?.tomorrowDosingHours ?? null);
    if (scheduleSummary.today.unscheduledLabels.length > 0) {
      return {
        dayLabel: "Not Scheduled Today",
        dateLabel: `Today · ${formatScheduleDateLabel(scheduleSummary.todayIsoDate)}`,
        statusLabel: "Not Scheduled",
        emphasis: "warning" as const,
        helpText: `You are not scheduled today, but tomorrow you have ${scheduleSummary.tomorrow.scheduledLabels.join(" + ")}${byLabel}.`,
        tomorrowLabel: scheduleSummary.tomorrow.scheduledLabels.join(" + "),
      };
    }
    return {
      dayLabel: "Tomorrow",
      dateLabel: `Tomorrow · ${formatScheduleDateLabel(scheduleSummary.tomorrowIsoDate)}`,
      statusLabel: scheduleSummary.tomorrow.scheduledLabels.join(" + "),
      emphasis: "watch" as const,
      helpText: `You are scheduled to visit the treatment center tomorrow${byLabel}.`,
    };
  }

  if (scheduleSummary.today.unscheduledLabels.length > 0) {
    return {
      dayLabel: "Not Scheduled Today",
      dateLabel: `Today · ${formatScheduleDateLabel(scheduleSummary.todayIsoDate)}`,
      statusLabel: "Not Scheduled",
      emphasis: "neutral" as const,
      helpText:
        "You are not scheduled for today. Tomorrow's schedule will be available after the next call completes.",
    };
  }

  return null;
}

/**
 * The monthly nudge only fires when the patient is not already booked for a
 * qualifying screen: chasing someone about a screen they have scheduled is
 * noise, and the today/tomorrow callout is the more urgent instruction.
 *
 * Delegates to the shared core helper so the dashboard and the SMS/email cannot
 * disagree about whether the nudge applies — including the month-boundary case,
 * where tomorrow's screen satisfies next month and this one is still unmet.
 */
export function shouldShowMonthlyScreenNudge(
  scheduleSummary: BhgScheduleSummary | null
): boolean {
  if (!scheduleSummary) return false;
  return (
    resolveMonthlyScreenNudgeUrgency({
      monthlyScreen: scheduleSummary.monthlyScreen,
      todayScheduledLabels: scheduleSummary.today.scheduledLabels,
      tomorrowScheduledLabels: scheduleSummary.tomorrow.scheduledLabels,
      tomorrowIsoDate: scheduleSummary.tomorrowIsoDate,
      ...(scheduleSummary.monthlyScreenLabels
        ? { monthlyScreenLabels: scheduleSummary.monthlyScreenLabels }
        : {}),
    }) !== "none"
  );
}

export function hasDisplayableCallError(call: Pick<BhgCallRecord, "status" | "errorReason">) {
  const status = String(call.status ?? "")
    .trim()
    .toLowerCase();
  const isFailurePath = status === "failed" || status.includes("timeout");
  return isFailurePath && Boolean(call.errorReason);
}

export function renderResultLabel(call: BhgCallRecord) {
  if (hasDisplayableCallError(call)) return call.errorReason;
  const status = String(call.status ?? "")
    .trim()
    .toLowerCase();
  const isTerminal = status === "completed" || status === "failed";
  if (!isTerminal) return "Pending";
  return call.resultLabel ?? "Pending";
}

function getCallOutcomeVariant(call: BhgCallRecord): OutcomeVariant {
  const status = String(call.status ?? "")
    .trim()
    .toLowerCase();
  if (status === "failed" || Boolean(call.errorReason)) return "fail";
  const result = (call.resultLabel ?? "").trim().toLowerCase();
  if (result === "technical difficulties") return "fail";
  if (result === "" || result === "not scheduled") {
    return status === "completed" ? "success" : "default";
  }
  return "danger";
}

function getCallResultPillTone(call: BhgCallRecord): PillTone {
  const variant = getCallOutcomeVariant(call);
  if (variant === "danger" || variant === "fail") return "fail";
  return "ok";
}

function getCallStatusPillTone(call: BhgCallRecord): PillTone {
  const status = String(call.status ?? "")
    .trim()
    .toLowerCase();
  if (status === "completed") return "ok";
  if (status === "failed") return "fail";
  return "default";
}

function getRelatedCallDotTone(call: BhgCallRecord): RelatedCallDotTone {
  const variant = getCallOutcomeVariant(call);
  if (variant === "danger" || variant === "fail") return "fail";
  return "ok";
}

function buildCallDetailHeadline(call: BhgCallRecord, clinic: BhgClinicOption | null): ReactNode {
  const variant = getCallOutcomeVariant(call);

  if (variant === "danger" && clinic) {
    const label = call.resultLabel?.trim() || "a scheduled visit";
    return (
      <>
        You are scheduled to visit <em>{clinic.name}</em> today for {label}.
      </>
    );
  }
  if (variant === "success") {
    return "You are not scheduled for today.";
  }
  if (variant === "fail") {
    return call.errorReason ?? "This call did not complete successfully.";
  }
  return renderResultLabel(call) ?? "Call complete";
}

function buildCallDetailSub(call: BhgCallRecord, dueByLabel: string | null): string | undefined {
  const variant = getCallOutcomeVariant(call);

  if (call.errorReason && variant === "fail") return undefined;

  if (variant === "danger" && dueByLabel && call.resultDate) {
    return `Go in before dosing closes at ${dueByLabel} on ${formatDateLabel(call.resultDate)}. Failure to comply with this request may affect your treatment status.`;
  }
  if (variant === "success") {
    return "Your latest check-in confirmed no scheduled visit. Tomorrow's schedule will update after the next call.";
  }
  return undefined;
}

function formatCallTime(startedAt: string) {
  const d = parseCallStartedAt(startedAt);
  if (!d) return startedAt;
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function isTechnicalDifficultyResultLabel(resultLabel: string | null | undefined) {
  return (
    String(resultLabel ?? "")
      .trim()
      .toLowerCase() === "technical difficulties"
  );
}

export function renderAudioAction(
  call: BhgCallRecord,
  availability: { available: boolean; note: string | null; checking: boolean } | undefined,
  buildRecordingDownloadUrl: (callSid: string) => string
) {
  if (!call.recordingAvailable) return <span className="cf-help">Not available.</span>;
  if (call.recordingExpired) return <span className="cf-help">Not available (expired).</span>;
  if (!availability || availability.checking)
    return <span className="cf-help">Checking audio availability...</span>;
  if (availability.available) {
    return (
      <a
        className="btn btn-secondary btn-sm"
        href={buildRecordingDownloadUrl(call.callSid)}
        download={`${call.callSid}.mp3`}
      >
        Download audio
      </a>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--cf-space-sm)",
        flexWrap: "wrap",
      }}
    >
      <button className="btn btn-secondary btn-sm" type="button" disabled>
        Download audio
      </button>
      <span className="cf-help">
        {availability.note ?? "Not available."} Audio can expire after a retention window.
      </span>
    </span>
  );
}

export function pickCallDurationSeconds(events: BhgCallEvent[]) {
  const toDuration = (event: BhgCallEvent) =>
    typeof event.callDurationSeconds === "number" && Number.isFinite(event.callDurationSeconds)
      ? event.callDurationSeconds
      : null;
  const statusDurations = events
    .filter((event) => typeof event.callStatus === "string" && event.callStatus.trim().length > 0)
    .map(toDuration)
    .filter((seconds): seconds is number => typeof seconds === "number")
    .sort((left, right) => right - left);
  if (statusDurations.length > 0) return statusDurations[0];
  const fallbackDurations = events
    .map(toDuration)
    .filter((seconds): seconds is number => typeof seconds === "number")
    .sort((left, right) => right - left);
  return fallbackDurations[0] ?? null;
}

function deriveCompletionTime(events: BhgCallEvent[], callTime: Date | null, callStatus: string) {
  const normalizedStatus = callStatus.trim().toLowerCase();
  if (normalizedStatus !== "completed" && normalizedStatus !== "failed") return null;
  const durationSeconds = pickCallDurationSeconds(events);
  if (callTime && typeof durationSeconds === "number") {
    return new Date(callTime.getTime() + durationSeconds * 1000).toISOString();
  }
  const latestEventMs =
    events.length > 0
      ? Math.max(...events.map((event) => new Date(event.createdAt).getTime()))
      : NaN;
  return Number.isFinite(latestEventMs) ? new Date(latestEventMs).toISOString() : null;
}

export function buildCallProgressSteps(
  events: BhgCallEvent[],
  callTime: Date | null,
  callStatus: string
): BhgCallProgressStep[] {
  const sorted = [...events].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
  const findStatusTime = (statuses: string[]) =>
    sorted.find((event) => {
      const status = (event.callStatus ?? "").trim().toLowerCase();
      return statuses.includes(status);
    });

  const queued =
    findStatusTime(["queued"])?.createdAt ?? (callTime ? callTime.toISOString() : null);
  const dialed = findStatusTime(["initiated", "ringing"])?.createdAt ?? null;
  const answered = findStatusTime(["in-progress", "processing"])?.createdAt ?? null;
  const complete =
    findStatusTime(["completed"])?.createdAt ?? deriveCompletionTime(sorted, callTime, callStatus);

  const statusNormalized = callStatus.trim().toLowerCase();
  const reached = {
    queued: Boolean(queued),
    dialed:
      Boolean(dialed) ||
      ["initiated", "processing", "completed", "failed"].includes(statusNormalized),
    answered: Boolean(answered) || ["processing", "completed", "failed"].includes(statusNormalized),
    complete: Boolean(complete) || ["completed", "failed"].includes(statusNormalized),
  };

  return [
    { id: "queued", label: "queued", icon: "◷", createdAt: queued, reached: reached.queued },
    { id: "dialed", label: "dialed", icon: "☎", createdAt: dialed, reached: reached.dialed },
    {
      id: "answered",
      label: "answered",
      icon: "✓",
      createdAt: answered,
      reached: reached.answered,
    },
    {
      id: "complete",
      label: "complete",
      icon: "■",
      createdAt: complete,
      reached: reached.complete,
    },
  ];
}

export function getDurationBetweenSteps(
  current: BhgCallProgressStep,
  next: BhgCallProgressStep | undefined
) {
  if (!next || !current.createdAt || !next.createdAt) return "—";
  const currentMs = new Date(current.createdAt).getTime();
  const nextMs = new Date(next.createdAt).getTime();
  if (!Number.isFinite(currentMs) || !Number.isFinite(nextMs) || nextMs < currentMs) return "—";
  return formatDuration(Math.round((nextMs - currentMs) / 1000));
}

export function buildDashboardCallSummary(calls: BhgCallRecord[]) {
  const completedCalls = calls.filter(
    (call) =>
      String(call.status ?? "")
        .trim()
        .toLowerCase() === "completed"
  );
  const nonCompletedCalls = calls.filter(
    (call) =>
      String(call.status ?? "")
        .trim()
        .toLowerCase() !== "completed"
  ).length;
  return {
    totalCalls: calls.length,
    completedCalls: completedCalls.length,
    nonCompletedCalls,
    latestOutcomeLabel: completedCalls[0]?.resultLabel ?? "No completed calls yet",
  };
}

function isStartedAtOnIsoDate(startedAt: string | undefined | null, isoDate: string | null) {
  if (!startedAt || !isoDate) return false;
  const d = parseCallStartedAt(startedAt);
  if (!d) return false;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}` === isoDate;
}

function formatCallRowTime(startedAt: string) {
  const d = parseCallStartedAt(startedAt);
  if (!d) return { primary: startedAt, secondary: undefined };
  return {
    primary: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    secondary: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  };
}

function DashboardOverlay(props: {
  open: boolean;
  label: string;
  onClose: () => void;
  children: ReactNode;
  narrow?: boolean;
}) {
  const { open, label, onClose, narrow, children } = props;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    background: "rgba(0,0,0,0.82)",
    backdropFilter: "blur(8px)",
  };

  const panelStyle: React.CSSProperties = {
    width: narrow ? "min(760px, 100%)" : "min(1080px, 100%)",
    maxHeight: "min(92vh, 1200px)",
    overflow: "auto",
    borderRadius: "var(--cf-radius-lg, 12px)",
    padding: "1rem",
    background: "var(--cf-surface, #fff)",
    border: "1px solid var(--cf-border-color, #e0e0e0)",
    boxShadow: "0 24px 72px rgba(0,0,0,0.48)",
    display: "grid",
    gap: "0.75rem",
  };

  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          style={overlayStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={onClose}
        >
          <motion.div
            style={panelStyle}
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.24, ease: "easeOut" }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function exportCallsToXlsx(calls: BhgCallRecord[], filename = "bhg-call-log.xlsx") {
  const rows = calls.map((call) => ({
    Date: call.startedAt
      ? new Date(call.startedAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "",
    Time: call.startedAt
      ? new Date(call.startedAt).toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        })
      : "",
    Result: call.resultLabel ?? "",
    "Result Date": call.resultDate ?? "",
    Status: call.status,
    Transcript: (call.transcript ?? "")
      .replace(/\[keys:[^\]]+\]/g, "")
      .replace(/\s+/g, " ")
      .trim(),
  }));
  const ws = utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 80 }];
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, "Call Log");
  writeFileXLSX(wb, filename);
}

export function BhgCallUserDashboardPageV2({
  provider = liveBhgCallDashboardProvider,
}: {
  provider?: BhgCallDashboardProvider;
} = {}) {
  const { pushToast } = useToast();
  const [calls, setCalls] = useState<BhgCallRecord[]>([]);
  const [scheduleSummary, setScheduleSummary] = useState<BhgScheduleSummary | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isFallbackPollingActive, setIsFallbackPollingActive] = useState(false);
  const fallbackPollingActiveRef = useRef(false);

  const [currentNotificationTime, setCurrentNotificationTime] = useState(
    DEFAULT_BHG_NOTIFICATION_TIME
  );
  const [selectedNotificationTime, setSelectedNotificationTime] = useState(
    DEFAULT_BHG_NOTIFICATION_TIME
  );
  const [currentNotificationTimezone, setCurrentNotificationTimezone] = useState(
    DEFAULT_BHG_NOTIFICATION_TIMEZONE
  );
  const [selectedNotificationTimezone, setSelectedNotificationTimezone] = useState(
    DEFAULT_BHG_NOTIFICATION_TIMEZONE
  );
  const [notificationChannels, setNotificationChannels] = useState<("sms" | "email")[]>([]);

  const [clinics, setClinics] = useState<BhgClinicOption[]>([]);
  const [clinicName, setClinicName] = useState<string | null>(null);
  const [currentClinicId, setCurrentClinicId] = useState("");
  const [selectedClinicId, setSelectedClinicId] = useState("");
  const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] = useState(false);
  const [isNotificationScheduleOpen, setIsNotificationScheduleOpen] = useState(false);
  const [isClinicSelectionOpen, setIsClinicSelectionOpen] = useState(false);
  const [currentPhone, setCurrentPhone] = useState("");
  const [selectedPhone, setSelectedPhone] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [currentChannels, setCurrentChannels] = useState<("sms" | "email")[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<("sms" | "email")[]>([]);
  const [currentConsent, setCurrentConsent] = useState(false);
  const [consentToMessages, setConsentToMessages] = useState(false);
  const [notificationSettingsSaving, setNotificationSettingsSaving] = useState(false);
  const [notificationSettingsMessages, setNotificationSettingsMessages] =
    useState<BhgNotificationSettingsMessages>({});
  const [notificationScheduleSaving, setNotificationScheduleSaving] = useState(false);
  const [notificationScheduleMessages, setNotificationScheduleMessages] =
    useState<BhgNotificationScheduleSettingsMessages>({});
  const [clinicSettingsSaving, setClinicSettingsSaving] = useState(false);
  const [clinicSettingsMessage, setClinicSettingsMessage] = useState<string | null>(null);
  const [accessStatus, setAccessStatus] = useState<"loading" | "allowed" | "denied">("loading");

  const [recordingAvailabilityByCallSid, setRecordingAvailabilityByCallSid] = useState<
    Record<string, { available: boolean; note: string | null; checking: boolean }>
  >({});
  const checkingCallSids = useRef<Set<string>>(new Set());

  const [callPage, setCallPage] = useState(1);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const CALLS_PER_PAGE = 10;

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isClinicDetailsExpanded, setIsClinicDetailsExpanded] = useState(false);
  const [selfReportedScreens, setSelfReportedScreens] = useState<BhgSelfReportedScreen[]>([]);
  const [isSelfReportOpen, setIsSelfReportOpen] = useState(false);
  const [selfReportDate, setSelfReportDate] = useState("");
  const [selfReportNote, setSelfReportNote] = useState("");
  const [selfReportSaving, setSelfReportSaving] = useState(false);
  const [selfReportError, setSelfReportError] = useState<string | null>(null);
  const notificationSettingsAvailableChannels = useMemo(
    () => ["sms", "email"] as ("sms" | "email")[],
    []
  );

  useEffect(() => {
    let mounted = true;
    void (async () => {
      let sub;
      try {
        sub = await provider.fetchSubscription();
      } catch {
        if (mounted) {
          setAccessStatus("denied");
        }
        return;
      }

      if (!mounted) return;
      if (!sub) {
        setAccessStatus("denied");
        return;
      }
      setAccessStatus("allowed");

      let clinicList: BhgClinicOption[] = [];
      let profile: { email: string; phoneNumber: string } | null = null;
      try {
        [clinicList, profile] = await Promise.all([
          provider.fetchClinics(),
          provider.fetchUserProfile?.() ?? Promise.resolve({ email: "", phoneNumber: "" }),
        ]);
      } catch {
        if (!mounted) return;
        setClinics([]);
        return;
      }
      if (!mounted) return;
      setClinics(clinicList);
      const enrollment = sub.bhgEnrollment;
      if (!enrollment) return;
      setClinicName(enrollment.clinicName ?? null);
      const matchedClinicByName = clinicList.find(
        (clinic) => clinic.name === enrollment.clinicName
      );
      const enrollmentClinicId =
        enrollment.clinicId != null
          ? String(enrollment.clinicId)
          : matchedClinicByName
            ? String(matchedClinicByName.id)
            : "";
      setCurrentClinicId(enrollmentClinicId);
      setSelectedClinicId(enrollmentClinicId);
      setCurrentNotificationTime(enrollment.notificationTime ?? DEFAULT_BHG_NOTIFICATION_TIME);
      setSelectedNotificationTime(enrollment.notificationTime ?? DEFAULT_BHG_NOTIFICATION_TIME);
      setCurrentNotificationTimezone(
        enrollment.notificationTimezone ?? DEFAULT_BHG_NOTIFICATION_TIMEZONE
      );
      setSelectedNotificationTimezone(
        enrollment.notificationTimezone ?? DEFAULT_BHG_NOTIFICATION_TIMEZONE
      );
      setNotificationChannels(enrollment.notificationChannels ?? []);
      const enrollmentPhone = normalizeUsPhoneNumber(enrollment.mobilePhone ?? "");
      const enrollmentEmail = enrollment.notificationEmail ?? "";
      const fallbackPhone = normalizeUsPhoneNumber(profile?.phoneNumber ?? "");
      const fallbackEmail = profile?.email ?? "";
      const defaultPhone = enrollmentPhone || fallbackPhone;
      const defaultEmail = enrollmentEmail || fallbackEmail;
      const defaultChannels =
        ((enrollment.notificationChannels ?? []) as ("sms" | "email")[]).length > 0
          ? ((enrollment.notificationChannels ?? []) as ("sms" | "email")[])
          : (["sms"] as ("sms" | "email")[]);
      setCurrentPhone(defaultPhone);
      setSelectedPhone(defaultPhone);
      setCurrentEmail(defaultEmail);
      setSelectedEmail(defaultEmail);
      setCurrentChannels(defaultChannels);
      setSelectedChannels(defaultChannels);
      const consentDefault = Boolean(defaultPhone.trim() || defaultEmail.trim());
      setCurrentConsent(consentDefault);
      setConsentToMessages(consentDefault);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (accessStatus !== "allowed") return;
    let mounted = true;
    void (async () => {
      try {
        const payload = await provider.fetchCalls();
        if (!mounted) return;
        setCalls(payload.calls);
        setScheduleSummary(payload.scheduleSummary);
        setUpdatedAt(payload.updatedAt);
        setLoadError(null);
      } catch {
        if (mounted) setLoadError("Unable to load call records right now.");
      }
    })();

    const unsubscribe = provider.subscribeCalls(
      (nextCalls, nextScheduleSummary, nextUpdatedAt) => {
        if (!mounted) return;
        setCalls(nextCalls);
        setScheduleSummary(nextScheduleSummary);
        setUpdatedAt(nextUpdatedAt);
        setLoadError(null);
        fallbackPollingActiveRef.current = false;
        setIsFallbackPollingActive(false);
      },
      () => {
        if (!mounted) return;
        setLoadError("Live updates disconnected. Refresh to reconnect.");
        if (!fallbackPollingActiveRef.current) {
          fallbackPollingActiveRef.current = true;
          setIsFallbackPollingActive(true);
        }
      }
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [accessStatus]);

  useEffect(() => {
    if (accessStatus !== "allowed") return;
    const load = provider.fetchSelfReportedScreens;
    if (!load) return;
    let mounted = true;
    void (async () => {
      try {
        const screens = await load();
        if (mounted) setSelfReportedScreens(screens);
      } catch {
        // Non-fatal: the rest of the dashboard is still useful, and the nudge
        // already reflects these rows via the server-computed summary.
      }
    })();
    return () => {
      mounted = false;
    };
  }, [accessStatus]);

  useEffect(() => {
    if (accessStatus !== "allowed") return;
    if (!isFallbackPollingActive) return;

    let mounted = true;
    const pollFallback = async () => {
      try {
        const payload = await provider.fetchCalls();
        if (!mounted) return;
        setCalls(payload.calls);
        setScheduleSummary(payload.scheduleSummary);
        setUpdatedAt(payload.updatedAt);
      } catch {
        // Keep last good state while fallback polling retries.
      }
    };

    void pollFallback();
    const interval = setInterval(() => {
      void pollFallback();
    }, FALLBACK_POLL_INTERVAL_MS);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [accessStatus, isFallbackPollingActive]);

  useEffect(() => {
    if (accessStatus !== "allowed") return;
    let active = true;
    const callSidsToCheck = calls
      .filter((call) => call.recordingAvailable && !call.recordingExpired)
      .map((call) => call.callSid)
      .filter((callSid) => !checkingCallSids.current.has(callSid));
    const introducedCallSids = new Set<string>();

    if (callSidsToCheck.length === 0) return;

    for (const callSid of callSidsToCheck) {
      checkingCallSids.current.add(callSid);
      introducedCallSids.add(callSid);
      setRecordingAvailabilityByCallSid((current) => ({
        ...current,
        [callSid]: current[callSid] ?? { available: false, note: null, checking: true },
      }));
    }

    void Promise.all(
      callSidsToCheck.map(async (callSid) => {
        try {
          const availability = await provider.fetchRecordingAvailability(callSid);
          if (!active) return;
          setRecordingAvailabilityByCallSid((current) => ({
            ...current,
            [callSid]: {
              available: availability.available,
              note: availability.note,
              checking: false,
            },
          }));
        } catch {
          if (!active) return;
          setRecordingAvailabilityByCallSid((current) => ({
            ...current,
            [callSid]: {
              available: false,
              note: "Audio availability could not be confirmed.",
              checking: false,
            },
          }));
        } finally {
          if (active) introducedCallSids.delete(callSid);
        }
      })
    );

    return () => {
      active = false;
      for (const callSid of introducedCallSids) {
        checkingCallSids.current.delete(callSid);
      }
    };
  }, [accessStatus, calls]);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [exportMenuOpen]);

  const handleExport = (range: "week" | "month" | "all") => {
    setExportMenuOpen(false);
    const now = new Date();
    let filtered = calls;
    let suffix = "all-time";
    if (range === "week") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = calls.filter((c) => new Date(c.startedAt) >= weekAgo);
      suffix = "this-week";
    } else if (range === "month") {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      filtered = calls.filter((c) => new Date(c.startedAt) >= monthAgo);
      suffix = "this-month";
    }
    exportCallsToXlsx(filtered, `bhg-call-log-${suffix}.xlsx`);
  };

  const paginatedCalls = useMemo(() => {
    const start = (callPage - 1) * CALLS_PER_PAGE;
    return calls.slice(start, start + CALLS_PER_PAGE);
  }, [calls, callPage]);

  const selectedClinic = useMemo(() => {
    if (currentClinicId) {
      const byId = clinics.find((clinic) => String(clinic.id) === currentClinicId);
      if (byId) return byId;
    }
    if (clinicName) {
      return clinics.find((clinic) => clinic.name === clinicName) ?? null;
    }
    return null;
  }, [clinics, currentClinicId, clinicName]);

  const todayWeekday = useMemo(
    () => getWeekdayKey(scheduleSummary?.todayIsoDate ?? null),
    [scheduleSummary?.todayIsoDate]
  );
  const tomorrowWeekday = useMemo(
    () => getWeekdayKey(scheduleSummary?.tomorrowIsoDate ?? null),
    [scheduleSummary?.tomorrowIsoDate]
  );
  const todayDosingHours = useMemo(
    () => getHoursForDay(selectedClinic?.dosingHours ?? null, todayWeekday),
    [selectedClinic?.dosingHours, todayWeekday]
  );
  const todayOperatingHours = useMemo(
    () => getHoursForDay(selectedClinic?.operatingHours ?? null, todayWeekday),
    [selectedClinic?.operatingHours, todayWeekday]
  );
  const tomorrowDosingHours = useMemo(
    () => getHoursForDay(selectedClinic?.dosingHours ?? null, tomorrowWeekday),
    [selectedClinic?.dosingHours, tomorrowWeekday]
  );

  const prominentScheduleCallout = useMemo(
    () =>
      getProminentScheduleCallout(scheduleSummary, {
        todayDosingHours,
        tomorrowDosingHours,
      }),
    [scheduleSummary, todayDosingHours, tomorrowDosingHours]
  );

  const liveUpdatesLabel = isFallbackPollingActive
    ? "Fallback polling"
    : loadError
      ? "Needs attention"
      : "Connected";
  const liveUpdatesHelp = isFallbackPollingActive
    ? "The live stream dropped, so the dashboard is refreshing on a timer."
    : formatLiveRefreshLabel(updatedAt);
  const notificationChannelsLabel = describeDashboardNotificationChannels(notificationChannels);
  const nextCallAtLabel = formatNextCallAtLabel(scheduleSummary, currentNotificationTime);
  const todayScheduleStatusLabel = formatScheduleStatusLabel({
    day: "today",
    scheduledLabels: scheduleSummary?.today.scheduledLabels ?? [],
    unscheduledLabels: scheduleSummary?.today.unscheduledLabels ?? [],
  });
  const tomorrowScheduleStatusLabel = formatScheduleStatusLabel({
    day: "tomorrow",
    scheduledLabels: scheduleSummary?.tomorrow.scheduledLabels ?? [],
    unscheduledLabels: scheduleSummary?.tomorrow.unscheduledLabels ?? [],
  });

  const latestCallSid = useMemo(() => {
    if (calls.length === 0) return null;
    const latest = [...calls].sort((a, b) => {
      const at = new Date(a.startedAt).getTime();
      const bt = new Date(b.startedAt).getTime();
      return bt - at;
    })[0];
    return latest?.callSid ?? null;
  }, [calls]);

  const openCallDetails = (callSid: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("callSid", callSid);
    navigate(
      {
        pathname: location.pathname,
        search: `?${next.toString()}`,
      },
      { replace: false }
    );
  };

  const selectedCallSid = searchParams.get("callSid");
  const selectedCall = useMemo(
    () => (selectedCallSid ? (calls.find((c) => c.callSid === selectedCallSid) ?? null) : null),
    [calls, selectedCallSid]
  );

  const [selectedCallEvents, setSelectedCallEvents] = useState<BhgCallEvent[]>([]);
  useEffect(() => {
    if (!selectedCallSid) {
      setSelectedCallEvents([]);
      return;
    }
    let active = true;
    void provider
      .fetchCallEvents(selectedCallSid)
      .then((res) => {
        if (active) setSelectedCallEvents(res.events);
      })
      .catch(() => {
        if (active) setSelectedCallEvents([]);
      });
    return () => {
      active = false;
    };
  }, [selectedCallSid]);

  const selectedCallDurationSeconds = useMemo(
    () => pickCallDurationSeconds(selectedCallEvents),
    [selectedCallEvents]
  );

  const closeCallDetails = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("callSid");
    navigate(
      { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" },
      { replace: true }
    );
  };

  const dueByLabel =
    prominentScheduleCallout?.dayLabel === "Today"
      ? (extractDosingEndTime(todayDosingHours) ?? "dosing end")
      : null;
  const latestCompletedCall = useMemo(() => {
    const completed = calls.filter(
      (call) =>
        String(call.status ?? "")
          .trim()
          .toLowerCase() === "completed"
    );
    return (
      [...completed].sort(
        (left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime()
      )[0] ?? null
    );
  }, [calls]);
  const hasCompletedCallToday =
    latestCompletedCall !== null &&
    scheduleSummary?.todayIsoDate != null &&
    latestCompletedCall.startedAt.startsWith(scheduleSummary.todayIsoDate);
  const latestTechnicalDifficulty = isTechnicalDifficultyResultLabel(
    latestCompletedCall?.resultLabel ?? null
  );
  const monthlyScreenStatus = scheduleSummary?.monthlyScreen ?? null;
  const showMonthlyScreenNudge = shouldShowMonthlyScreenNudge(scheduleSummary);
  const monthlyScreenMessage = showMonthlyScreenNudge
    ? buildMonthlyScreenMessage(monthlyScreenStatus)
    : null;
  const monthlyScreenHeadline = showMonthlyScreenNudge
    ? buildMonthlyScreenHeadline(monthlyScreenStatus)
    : null;
  const canSelfReportScreens = typeof provider.recordSelfReportedScreen === "function";
  const monthPrefix = scheduleSummary?.todayIsoDate?.slice(0, 7) ?? "";
  const selfReportsThisMonth = useMemo(
    () =>
      monthPrefix
        ? selfReportedScreens.filter(
            (screen) => !screen.voidedAt && screen.screenDate.startsWith(monthPrefix)
          )
        : [],
    [selfReportedScreens, monthPrefix]
  );
  const selfReportedDatesThisMonth = selfReportsThisMonth
    .map((screen) => screen.screenDate)
    .join(", ");

  async function submitSelfReportedScreen() {
    const record = provider.recordSelfReportedScreen;
    if (!record) return;
    setSelfReportSaving(true);
    setSelfReportError(null);
    try {
      const result = await record({
        // Blank means "today" — the server resolves it in the patient's own
        // timezone, which the browser's clock can disagree with.
        ...(selfReportDate ? { screenDate: selfReportDate } : {}),
        ...(selfReportNote.trim() ? { note: selfReportNote.trim() } : {}),
      });
      if (!result.ok) {
        setSelfReportError(result.error);
        return;
      }
      setSelfReportedScreens((previous) => {
        const withoutDuplicate = previous.filter((screen) => screen.id !== result.screen.id);
        return [result.screen, ...withoutDuplicate].sort((left, right) =>
          right.screenDate.localeCompare(left.screenDate)
        );
      });
      setIsSelfReportOpen(false);
      setSelfReportDate("");
      setSelfReportNote("");
      pushToast("Screen recorded. Keep this if the clinic ever asks.", "success");
      // Re-fetch so the nudge and the at-a-glance row reflect the new row; the
      // monthly status is computed server-side, not derived from local state.
      try {
        const payload = await provider.fetchCalls();
        setCalls(payload.calls);
        setScheduleSummary(payload.scheduleSummary);
        setUpdatedAt(payload.updatedAt);
      } catch {
        // The record saved; a stale summary corrects itself on the next tick.
      }
    } catch {
      setSelfReportError("Could not save your screen record. Try again.");
    } finally {
      setSelfReportSaving(false);
    }
  }

  const calloutEmphasis = latestTechnicalDifficulty
    ? ("warning" as const)
    : (prominentScheduleCallout?.emphasis ?? null);
  const hasUrgentAction = calloutEmphasis === "urgent";

  const heroState: "scheduled" | "report" | undefined = latestTechnicalDifficulty
    ? "report"
    : calloutEmphasis === "urgent"
      ? "scheduled"
      : calloutEmphasis === "warning" || showMonthlyScreenNudge
        ? "report"
        : undefined;

  if (accessStatus === "loading") {
    return (
      <div className="page ds-stack">
        <PageHeader title="Loading your dashboard" sub="Checking your subscription status." />
        <Card>
          <div className="card-body" style={{ textAlign: "center" }}>
            <ProgressBar label="Loading dashboard" />
          </div>
        </Card>
      </div>
    );
  }

  if (accessStatus === "denied") {
    return <Navigate to={`/${BHG_ROUTES.SIGNUP}`} replace />;
  }

  if (selectedCall) {
    return (
      <CallDetailPage>
        <CallDetailNav
          onBack={closeCallDetails}
          crumb={
            <>
              Recent calls ·{" "}
              <b>
                #{selectedCall.id} · {formatDateLabel(selectedCall.startedAt)}
              </b>
            </>
          }
          actions={
            selectedCall.recordingAvailable && !selectedCall.recordingExpired
              ? renderAudioAction(
                  selectedCall,
                  recordingAvailabilityByCallSid[selectedCall.callSid],
                  provider.buildRecordingDownloadUrl
                )
              : null
          }
        />

        <CallDetailBody>
          <OutcomeHero
            variant={getCallOutcomeVariant(selectedCall)}
            kickerLabel={<>Result · {renderResultLabel(selectedCall)}</>}
            headline={buildCallDetailHeadline(selectedCall, selectedClinic)}
            sub={buildCallDetailSub(selectedCall, dueByLabel)}
            meta={[
              { label: "Called", value: formatCallTime(selectedCall.startedAt) },
              ...(selectedCallDurationSeconds != null
                ? [{ label: "Duration", value: formatDuration(selectedCallDurationSeconds) }]
                : []),
              {
                label: "Result",
                value: (
                  <ResultPill tone={getCallResultPillTone(selectedCall)}>
                    {renderResultLabel(selectedCall)}
                  </ResultPill>
                ),
              },
              {
                label: "Status",
                value: (
                  <ResultPill tone={getCallStatusPillTone(selectedCall)}>
                    {selectedCall.status}
                  </ResultPill>
                ),
              },
            ]}
            actions={
              <>
                {selectedCall.recordingAvailable && !selectedCall.recordingExpired
                  ? renderAudioAction(
                      selectedCall,
                      recordingAvailabilityByCallSid[selectedCall.callSid],
                      provider.buildRecordingDownloadUrl
                    )
                  : null}
                <span className="spacer" />
                <span className="hint">
                  {selectedCall.recordingAvailable
                    ? "Transcript retained indefinitely. Audio retained 30 days."
                    : "No recording available for this call."}
                </span>
              </>
            }
          />

          {getCallOutcomeVariant(selectedCall) === "danger" && selectedClinic && dueByLabel ? (
            <NextStepBanner
              title={`Arrive at ${selectedClinic.name} before ${dueByLabel}.`}
              description={
                <>
                  {selectedClinic.streetAddress} · {selectedClinic.city}, {selectedClinic.state}{" "}
                  {selectedClinic.postalCode} · {selectedClinic.phone}
                </>
              }
              action={
                <a
                  className="btn btn-danger btn-sm"
                  href={`https://maps.google.com/?q=${encodeURIComponent(`${selectedClinic.streetAddress}, ${selectedClinic.city}, ${selectedClinic.state} ${selectedClinic.postalCode}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get directions
                </a>
              }
            />
          ) : null}

          <CallDetailColumns>
            <Card>
              <CardHeader
                title="Full transcript"
                meta={selectedCall.status === "completed" ? "auto-transcribed" : undefined}
              />
              {selectedCall.recordingAvailable && !selectedCall.recordingExpired ? (
                <AudioPlayer
                  durationSeconds={selectedCallDurationSeconds ?? 0}
                  downloadUrl={
                    recordingAvailabilityByCallSid[selectedCall.callSid]?.available
                      ? provider.buildRecordingDownloadUrl(selectedCall.callSid)
                      : null
                  }
                  downloadFilename={`${selectedCall.callSid}.mp3`}
                />
              ) : null}
              <CallDetailTranscript
                text={selectedCall.transcript}
                ivrLabel="BHG IVR"
                usLabel="ConstructorFleet"
              />
            </Card>

            <CallDetailRail>
              <Card>
                <CardHeader title="Call details" />
                <KvTable>
                  <KvRow label="Called" value={formatCallTime(selectedCall.startedAt)} />
                  {selectedCall.resultDate ? (
                    <KvRow label="Result" value={selectedCall.resultDate} />
                  ) : null}
                  {selectedCall.voiceProvider ? (
                    <KvRow label="Provider" value={selectedCall.voiceProvider} />
                  ) : null}
                  <KvRow label="Call SID" value={selectedCall.callSid} masked />
                  {selectedCall.errorReason ? (
                    <KvRow label="Error" value={selectedCall.errorReason} />
                  ) : null}
                  <KvRow
                    label="Recording"
                    value={
                      selectedCall.recordingAvailable
                        ? selectedCall.recordingExpired
                          ? "Expired"
                          : "Available"
                        : "Not available"
                    }
                  />
                </KvTable>
              </Card>

              <RelatedCallsCard
                title="Other calls this week"
                action={
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      closeCallDetails();
                    }}
                    style={{
                      fontSize: 12,
                      color: "var(--cf-brand)",
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    All calls →
                  </a>
                }
              >
                {calls.slice(0, 5).map((call) => (
                  <RelatedCallRow
                    key={call.callSid}
                    tone={getRelatedCallDotTone(call)}
                    title={renderResultLabel(call) ?? "Pending"}
                    subtitle={`${call.status} · ${formatDateLabel(call.startedAt)}`}
                    time={formatCallRowTime(call.startedAt).primary}
                    active={call.callSid === selectedCall.callSid}
                    onClick={() => openCallDetails(call.callSid)}
                  />
                ))}
              </RelatedCallsCard>
            </CallDetailRail>
          </CallDetailColumns>
        </CallDetailBody>
      </CallDetailPage>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="BHG Check-in Assist"
        sub="Today's schedule, your call results, and a record of every check-in."
        meta={{
          label: "Today",
          value: new Date().toLocaleDateString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          subValue: `${new Date().toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
          })} · ${currentNotificationTimezone}`,
        }}
      />

      <AnimatePresence>
        {loadError ? (
          <motion.div
            key="load-error"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{ overflow: "hidden" }}
          >
            <InlineAlert tone="danger">{loadError}</InlineAlert>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {monthlyScreenMessage ? (
          <motion.div
            key="monthly-screen-nudge"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{ overflow: "hidden" }}
          >
            <InlineAlert tone={monthlyScreenStatus?.urgency === "urgent" ? "danger" : "info"}>
              <strong>{monthlyScreenHeadline}</strong> {monthlyScreenMessage}
              {selectedClinic ? <> Call {selectedClinic.phone}.</> : null}
              {canSelfReportScreens ? (
                <div style={{ marginTop: 8 }}>
                  <Button size="sm" variant="secondary" onClick={() => setIsSelfReportOpen(true)}>
                    I already got screened
                  </Button>
                </div>
              ) : null}
            </InlineAlert>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <DashboardHero state={heroState} cols={3}>
        <HeroCell
          kickerIcon={
            latestTechnicalDifficulty || calloutEmphasis === "urgent" ? (
              <IconAlert style={{ width: 14, height: 14 }} />
            ) : (
              <IconCalendar style={{ width: 14, height: 14 }} />
            )
          }
          kickerLabel={
            latestTechnicalDifficulty
              ? "Clinic line issue"
              : calloutEmphasis === "urgent"
                ? "Action required"
                : "Visit status"
          }
          big={
            latestTechnicalDifficulty
              ? "Technical difficulties"
              : prominentScheduleCallout
                ? prominentScheduleCallout.statusLabel
                : hasCompletedCallToday
                  ? "Not scheduled"
                  : "No action yet"
          }
          bigTone={latestTechnicalDifficulty || calloutEmphasis === "urgent" ? "danger" : "default"}
          desc={
            latestTechnicalDifficulty ? (
              "The clinic line reported technical difficulties. This alert will stay visible until the issue is resolved."
            ) : prominentScheduleCallout?.dayLabel === "Today" ? (
              <>
                You are scheduled to visit <em>{selectedClinic?.name ?? "the clinic"}</em> today for{" "}
                {prominentScheduleCallout.statusLabel}
                {dueByLabel ? <> before dosing closes at {dueByLabel}</> : null}.
              </>
            ) : prominentScheduleCallout?.dayLabel === "Tomorrow" ? (
              <>
                You are scheduled to visit <em>{selectedClinic?.name ?? "the clinic"}</em> tomorrow
                for {prominentScheduleCallout.statusLabel}
                {dueByLabel ? <> before dosing closes at {dueByLabel}</> : null}.
              </>
            ) : prominentScheduleCallout?.dayLabel === "Not Scheduled Today" ? (
              prominentScheduleCallout.helpText
            ) : hasCompletedCallToday ? (
              "You're not scheduled for today based on your latest call."
            ) : (
              "Your schedule will appear after the next call completes."
            )
          }
          cell="visit"
        />
        <HeroCell
          kickerIcon={<IconCheck style={{ width: 14, height: 14 }} />}
          kickerLabel="Today's check-in"
          big={
            latestCompletedCall && hasCompletedCallToday ? "Call completed." : "Waiting for call"
          }
          bigTone={hasCompletedCallToday ? "success" : "default"}
          desc={
            hasCompletedCallToday
              ? "Your call went through. The result is recorded below and the transcript is saved."
              : `Your next call is scheduled ${nextCallAtLabel}. Make sure your contact details are up to date.`
          }
          cell="checkin"
        />
        <HeroCell
          kickerIcon={<IconClock style={{ width: 14, height: 14 }} />}
          kickerLabel="Clinic dosing"
          big={
            dueByLabel
              ? `Closes ${dueByLabel}`
              : todayDosingHours
                ? todayDosingHours
                : "Hours not set"
          }
          desc={
            selectedClinic
              ? `${selectedClinic.name} ends dosing${dueByLabel ? ` at ${dueByLabel}` : ""} today. Plan to arrive before closing.`
              : "Select a clinic to see dosing hours."
          }
          cell="dosing"
        />
      </DashboardHero>

      <div className="grid">
        <Card>
          <CardHeader
            title="Clinic snapshot"
            action={
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  setIsNotificationSettingsOpen(false);
                  setIsNotificationScheduleOpen(false);
                  setSelectedClinicId(currentClinicId);
                  setClinicSettingsMessage(null);
                  setIsClinicSelectionOpen(true);
                }}
              >
                Edit clinic →
              </a>
            }
          />
          <AnimatePresence mode="wait">
            {selectedClinic ? (
              <motion.div
                key="clinic-data"
                className="clinic"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
              >
                <div>
                  <h4 className="clinic-name">{selectedClinic.name}</h4>
                  <p className="clinic-addr">
                    {selectedClinic.streetAddress}, {selectedClinic.city}, {selectedClinic.state}{" "}
                    {selectedClinic.postalCode}
                    <br />
                    <span className="ph">{selectedClinic.phone}</span>
                  </p>
                </div>
                <div className="hours">
                  <div>
                    <div className="lbl">Operating hours</div>
                    <div className="val">{todayOperatingHours ?? "Not available"}</div>
                  </div>
                  <div>
                    <div className="lbl">Medication hours</div>
                    <div className="val">{todayDosingHours ?? "Not available"}</div>
                  </div>
                </div>
                <a
                  className="clinic-link"
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    setIsClinicDetailsExpanded((current) => !current);
                  }}
                >
                  {isClinicDetailsExpanded ? "Hide full hours" : "View clinic details"}{" "}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </a>
                <AnimatePresence initial={false}>
                  {isClinicDetailsExpanded ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: "easeOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="clinic-hours-row" style={{ marginTop: "0.5rem" }}>
                        {selectedClinic?.operatingHours ? (
                          <ClinicHoursBlock
                            label="Operating Hours"
                            hours={parseHoursJson(selectedClinic.operatingHours) ?? {}}
                          />
                        ) : null}
                        {selectedClinic?.dosingHours ? (
                          <ClinicHoursBlock
                            label="Medication Hours"
                            hours={parseHoursJson(selectedClinic.dosingHours) ?? {}}
                          />
                        ) : null}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div
                key="no-clinic"
                className="clinic"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <p className="hc-desc">
                  No clinic selected yet. Finish setting up your account to see your schedule here.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        <AtAGlance title="At a glance" meta={formatLiveRefreshLabel(updatedAt)}>
          {latestTechnicalDifficulty ? (
            <GlanceRow
              icon={<IconAlert />}
              iconTone="mute"
              title="Technical difficulties reported"
              subtitle="The latest call reported technical difficulties with the clinic line. This alert will stay visible instead of a normal result."
              value="Active"
            />
          ) : null}
          {monthlyScreenStatus ? (
            <GlanceRow
              icon={monthlyScreenStatus.satisfied ? <IconCheck /> : <IconAlert />}
              iconTone={monthlyScreenStatus.satisfied ? "brand" : "mute"}
              title="Drug screen this month"
              subtitle={
                // Never present a self-report as a clinic confirmation. The
                // patient needs to know which kind of record they're holding
                // before they rely on it in a conversation with the clinic.
                monthlyScreenStatus.satisfiedBy === "clinic"
                  ? "The clinic scheduled a screen this month."
                  : monthlyScreenStatus.satisfiedBy === "self-report"
                    ? // Naming the date is the point: it is what the patient
                      // repeats back if the clinic says they never came in.
                      `You recorded that you were screened${
                        selfReportedDatesThisMonth ? ` on ${selfReportedDatesThisMonth}` : ""
                      }. This is your own record, not the clinic's.`
                    : `No screen on record. ${monthlyScreenStatus.daysRemaining} day${monthlyScreenStatus.daysRemaining !== 1 ? "s" : ""} left in the month.`
              }
              value={
                monthlyScreenStatus.satisfiedBy === "clinic"
                  ? "Done"
                  : monthlyScreenStatus.satisfiedBy === "self-report"
                    ? "Self-reported"
                    : "None yet"
              }
            />
          ) : null}
          <GlanceRow
            icon={<IconCalendar />}
            iconTone="mute"
            title="Tomorrow's visit"
            subtitle="Status will update after the next completed call"
            value={tomorrowScheduleStatusLabel}
            valueNote="pending"
            aria-label="schedule status"
          />
          <GlanceRow
            icon={<IconClock />}
            title="Next call"
            subtitle={`${formatDateLabel(scheduleSummary?.tomorrowIsoDate ?? "")} — based on your schedule`}
            value={nextCallAtLabel}
          />
          <GlanceRow
            icon={<IconBell />}
            iconTone="brand"
            title="Notification delivery"
            subtitle={`${notificationChannelsLabel} · daily at ${currentNotificationTime} (${currentNotificationTimezone})`}
            value={notificationChannelsLabel === "Needs setup" ? "Off" : "On"}
            valueNote={
              notificationChannels.length > 0
                ? `${notificationChannels.length} channel${notificationChannels.length !== 1 ? "s" : ""}`
                : undefined
            }
          />
          <div style={{ display: "flex", gap: "0.5rem", padding: "0.25rem 0" }}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setIsNotificationScheduleOpen(true);
                setIsNotificationSettingsOpen(false);
                setIsClinicSelectionOpen(false);
              }}
            >
              Notification time
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setIsNotificationSettingsOpen(true);
                setIsNotificationScheduleOpen(false);
                setIsClinicSelectionOpen(false);
              }}
            >
              Notification settings
            </Button>
            {canSelfReportScreens ? (
              // Reachable whether or not the nudge is showing: the record is
              // most wanted after the fact, when a dispute comes up, and by
              // then the nudge that offered it is long gone.
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setIsSelfReportOpen(true);
                  setIsNotificationScheduleOpen(false);
                  setIsNotificationSettingsOpen(false);
                  setIsClinicSelectionOpen(false);
                }}
              >
                {selfReportsThisMonth.length > 0 ? "Your screen records" : "I already got screened"}
              </Button>
            ) : null}
          </div>
        </AtAGlance>
      </div>

      <RecentCallLog
        title="Recent calls"
        meta={`${calls.length} call${calls.length !== 1 ? "s" : ""}`}
        action={
          calls.length > 0 ? (
            <div ref={exportMenuRef} style={{ position: "relative" }}>
              <Button size="sm" variant="secondary" onClick={() => setExportMenuOpen((o) => !o)}>
                Export ▾
              </Button>
              {exportMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "100%",
                    marginTop: 4,
                    background: "var(--surface-1, #fff)",
                    border: "1px solid var(--border, #e2e2e2)",
                    borderRadius: 6,
                    boxShadow: "0 4px 12px rgba(0,0,0,.12)",
                    zIndex: 10,
                    minWidth: 160,
                    overflow: "hidden",
                  }}
                >
                  {(
                    [
                      ["week", "This Week"],
                      ["month", "This Month"],
                      ["all", "All Time"],
                    ] as const
                  ).map(([range, label]) => (
                    <button
                      key={range}
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "0.5rem 0.75rem",
                        borderRadius: 0,
                      }}
                      onClick={() => handleExport(range)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null
        }
        rows={paginatedCalls.map((call) => {
          const variant = getCallOutcomeVariant(call);
          const rail =
            variant === "danger"
              ? ("fail" as const)
              : variant === "fail"
                ? ("fail" as const)
                : ("ok" as const);
          const pillTone =
            variant === "danger"
              ? ("fail" as const)
              : variant === "fail"
                ? ("fail" as const)
                : ("ok" as const);
          return {
            rail,
            title: renderResultLabel(call) ?? "Pending",
            pill: {
              label: call.status,
              tone: pillTone,
            },
            snippet: call.transcript,
            when: formatCallRowTime(call.startedAt),
            onClick: () => openCallDetails(call.callSid),
          };
        })}
        pagination={{
          page: callPage,
          pageSize: CALLS_PER_PAGE,
          total: calls.length,
          onPageChange: setCallPage,
        }}
        wide
      />

      <DashboardOverlay
        open={isNotificationSettingsOpen}
        label="Notification settings"
        onClose={() => setIsNotificationSettingsOpen(false)}
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsNotificationSettingsOpen(false)}
          >
            Close
          </Button>
        </div>
        <BhgNotificationSettings
          disableInitialSync={true}
          onNotificationSettingsUpdated={({ notificationChannels }) => {
            setNotificationChannels(notificationChannels);
            setIsNotificationSettingsOpen(false);
            pushToast("Notification settings saved.", "success");
          }}
          provider={provider}
          availableChannels={notificationSettingsAvailableChannels}
          currentPhone={currentPhone}
          setCurrentPhone={setCurrentPhone}
          selectedPhone={selectedPhone}
          setSelectedPhone={setSelectedPhone}
          currentEmail={currentEmail}
          setCurrentEmail={setCurrentEmail}
          selectedEmail={selectedEmail}
          setSelectedEmail={setSelectedEmail}
          currentChannels={currentChannels}
          setCurrentChannels={setCurrentChannels}
          selectedChannels={selectedChannels}
          setSelectedChannels={setSelectedChannels}
          currentConsent={currentConsent}
          setCurrentConsent={setCurrentConsent}
          consentToMessages={consentToMessages}
          setConsentToMessages={setConsentToMessages}
          saving={notificationSettingsSaving}
          setSaving={setNotificationSettingsSaving}
          message={notificationSettingsMessages}
          setMessage={setNotificationSettingsMessages}
        />
      </DashboardOverlay>

      <DashboardOverlay
        open={isNotificationScheduleOpen}
        label="Notification schedule"
        onClose={() => setIsNotificationScheduleOpen(false)}
        narrow
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsNotificationScheduleOpen(false)}
          >
            Close
          </Button>
        </div>
        <BhgNotificationScheduleSettings
          disableInitialSync={true}
          provider={provider}
          dosingHoursJson={selectedClinic?.dosingHours ?? null}
          currentTime={currentNotificationTime}
          setCurrentTime={setCurrentNotificationTime}
          currentTimezone={currentNotificationTimezone}
          setCurrentTimezone={setCurrentNotificationTimezone}
          selectedTime={selectedNotificationTime}
          setSelectedTime={setSelectedNotificationTime}
          selectedTimezone={selectedNotificationTimezone}
          setSelectedTimezone={setSelectedNotificationTimezone}
          saving={notificationScheduleSaving}
          setSaving={setNotificationScheduleSaving}
          message={notificationScheduleMessages}
          setMessage={setNotificationScheduleMessages}
          onNotificationScheduleUpdated={({ notificationTime, notificationTimezone }) => {
            setCurrentNotificationTime(notificationTime);
            setSelectedNotificationTime(notificationTime);
            setCurrentNotificationTimezone(notificationTimezone);
            setSelectedNotificationTimezone(notificationTimezone);
            setIsNotificationScheduleOpen(false);
            pushToast("Notification time saved.", "success");
          }}
        />
      </DashboardOverlay>

      <DashboardOverlay
        open={isSelfReportOpen}
        label="Record a drug screen"
        onClose={() => setIsSelfReportOpen(false)}
        narrow
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="sm" variant="secondary" onClick={() => setIsSelfReportOpen(false)}>
            Close
          </Button>
        </div>
        <Card>
          <CardHeader title="Record a drug screen" />
          <p className="hc-desc">
            Use this when you went in for a screen the clinic never called you about. We save the
            date you enter along with the date and time you told us, so you have a dated record if
            the clinic later says you did not come in.
          </p>
          <p className="hc-desc">
            <strong>This is your own record, not the clinic&apos;s.</strong> It does not replace
            their files, but a note you made at the time — next to a call log showing you were never
            scheduled — is a lot better than remembering it later.
          </p>
          <div className="ds-stack">
            <FormField
              label="Date you were screened"
              htmlFor="self-report-date"
              helpText="Leave blank for today."
            >
              <TextInput
                id="self-report-date"
                type="date"
                value={selfReportDate}
                max={scheduleSummary?.todayIsoDate}
                onChange={(event) => setSelfReportDate(event.target.value)}
              />
            </FormField>
            <FormField
              label="Note (optional)"
              htmlFor="self-report-note"
              helpText="Anything worth remembering — who you spoke to, what you were told."
            >
              <TextArea
                id="self-report-note"
                rows={3}
                maxLength={500}
                value={selfReportNote}
                onChange={(event) => setSelfReportNote(event.target.value)}
              />
            </FormField>
            {selfReportError ? <InlineAlert tone="danger">{selfReportError}</InlineAlert> : null}
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                size="sm"
                onClick={() => void submitSelfReportedScreen()}
                disabled={selfReportSaving}
              >
                {selfReportSaving ? "Saving…" : "Save record"}
              </Button>
            </div>
          </div>

          {selfReportedScreens.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <CardHeader title="Your recorded screens" />
              <KvTable>
                {selfReportedScreens.map((screen) => (
                  <KvRow
                    key={screen.id}
                    label={screen.screenDate}
                    value={
                      <span>
                        {screen.voidedAt ? "Marked as a mistake" : "Recorded"}
                        {screen.note ? ` — ${screen.note}` : ""}
                        {!screen.voidedAt && provider.voidSelfReportedScreen ? (
                          <>
                            {" "}
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                const voidScreen = provider.voidSelfReportedScreen;
                                if (!voidScreen) return;
                                void (async () => {
                                  if (await voidScreen(screen.id)) {
                                    setSelfReportedScreens((previous) =>
                                      previous.map((entry) =>
                                        entry.id === screen.id
                                          ? { ...entry, voidedAt: new Date().toISOString() }
                                          : entry
                                      )
                                    );
                                  }
                                })();
                              }}
                            >
                              This was a mistake
                            </Button>
                          </>
                        ) : null}
                      </span>
                    }
                  />
                ))}
              </KvTable>
            </div>
          ) : null}
        </Card>
      </DashboardOverlay>

      <DashboardOverlay
        open={isClinicSelectionOpen}
        label="Clinic settings"
        onClose={() => setIsClinicSelectionOpen(false)}
        narrow
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="sm" variant="secondary" onClick={() => setIsClinicSelectionOpen(false)}>
            Close
          </Button>
        </div>
        <BhgClinicSettings
          context="dashboard"
          disableInitialSync={true}
          provider={provider}
          onClinicUpdated={() => {
            setClinicSettingsMessage("Clinic updated successfully.");
          }}
          clinics={clinics}
          setClinics={setClinics}
          currentClinicId={currentClinicId}
          setCurrentClinicId={setCurrentClinicId}
          selectedClinicId={selectedClinicId}
          setSelectedClinicId={setSelectedClinicId}
          clinicName={clinicName}
          setClinicName={setClinicName}
          saving={clinicSettingsSaving}
          setSaving={setClinicSettingsSaving}
          message={clinicSettingsMessage}
          setMessage={setClinicSettingsMessage}
        />
      </DashboardOverlay>
    </div>
  );
}
